import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as api from '../services/api';
import type { PublicRegistrationForm, RegistrationFormField } from '../types/registration';

type AnswerMap = Record<string, string | boolean>;

export function PublicApplyPage() {
  const { formId = '' } = useParams<{ formId: string }>();
  const [form, setForm] = useState<PublicRegistrationForm | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const sortedFields = useMemo(
    () => (form ? [...form.fields].sort((a, b) => a.order - b.order) : []),
    [form]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!formId) {
        setError('Missing registration form id');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const publicForm = await api.getPublicRegistrationForm(formId);
        if (!cancelled) {
          setForm(publicForm);
          const initial: AnswerMap = {};
          publicForm.fields.forEach((field) => {
            initial[field.key] = field.type === 'checkbox' ? false : '';
          });
          setAnswers(initial);
        }
      } catch (err) {
        if (!cancelled) {
          setForm(null);
          setError(
            err instanceof Error
              ? err.message
              : 'This registration form is not available'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [formId]);

  function setAnswer(field: RegistrationFormField, value: string | boolean) {
    setAnswers((current) => ({ ...current, [field.key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setSubmitting(true);
    setError(null);
    try {
      const payloadAnswers = sortedFields
        .map((field) => {
          const value = answers[field.key];
          if (field.type === 'checkbox') {
            return { key: field.key, value: Boolean(value) };
          }
          if (field.type === 'number') {
            const text = String(value ?? '').trim();
            if (!text) return null;
            return { key: field.key, value: Number(text) };
          }
          const text = String(value ?? '').trim();
          if (!text) return null;
          return { key: field.key, value: text };
        })
        .filter((item): item is { key: string; value: string | number | boolean } => item !== null);

      await api.submitRegistration({ formId: form.id, answers: payloadAnswers });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit registration');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-shell public-apply-shell">
      <section className="auth-panel public-apply-panel">
        <p className="brand-mark">The Foundation Movement</p>
        {loading ? <p className="muted">Loading registration form…</p> : null}

        {!loading && !form ? (
          <>
            <h1>Registration unavailable</h1>
            <p className="auth-subtitle">
              {error || 'This form is closed or no longer accepting applications.'}
            </p>
          </>
        ) : null}

        {!loading && form && submitted ? (
          <>
            <h1>Application received</h1>
            <p className="auth-subtitle">
              Thank you. Your application for “{form.title}” was submitted successfully. The school
              will review it shortly.
            </p>
          </>
        ) : null}

        {!loading && form && !submitted ? (
          <>
            <h1>{form.title}</h1>
            {form.description ? <p className="auth-subtitle">{form.description}</p> : (
              <p className="auth-subtitle">Complete the fields below to submit your application.</p>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
              {sortedFields.map((field) => (
                <label key={field.key}>
                  {field.label}
                  {field.required ? ' *' : ''}
                  {field.type === 'textarea' ? (
                    <textarea
                      value={String(answers[field.key] ?? '')}
                      onChange={(e) => setAnswer(field, e.target.value)}
                      required={field.required}
                      rows={4}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      value={String(answers[field.key] ?? '')}
                      onChange={(e) => setAnswer(field, e.target.value)}
                      required={field.required}
                    >
                      <option value="">Select…</option>
                      {(field.options ?? []).map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'checkbox' ? (
                    <span className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={Boolean(answers[field.key])}
                        onChange={(e) => setAnswer(field, e.target.checked)}
                        required={field.required}
                      />
                      <span>Yes</span>
                    </span>
                  ) : (
                    <input
                      type={
                        field.type === 'number'
                          ? 'number'
                          : field.type === 'email'
                            ? 'email'
                            : field.type === 'date'
                              ? 'date'
                              : field.type === 'phone'
                                ? 'tel'
                                : 'text'
                      }
                      value={String(answers[field.key] ?? '')}
                      onChange={(e) => setAnswer(field, e.target.value)}
                      required={field.required}
                    />
                  )}
                </label>
              ))}

              {error ? <p className="form-error" role="alert">{error}</p> : null}

              <button type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit application'}
              </button>
            </form>
          </>
        ) : null}

        <p className="public-apply-footer">
          <Link to="/login">School admin sign in</Link>
        </p>
      </section>
    </main>
  );
}
